import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import { Media, MediaProps } from '@/components/base/Media'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import TypographyMask from '@/components/base/TypographyMask'

import { mediaBannerRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type MediaBannerProps = HTMLAttributes<HTMLDivElement> & {
  title: string
  subtitle: string
  text: string
  primary_button: WPLink
  secondary_button: WPLink
  media: MediaProps
}

const MediaBanner = ({
  title,
  subtitle,
  text,
  primary_button,
  secondary_button,
  media,
  ...props
}: MediaBannerProps) => {
  return (
    <Motion animation={mediaBannerRevealAnimation}>
      <Section {...props}>
        <Container>
          <Grid className="items-center">
            <div className="col-span-4 flex flex-col gap-spacing-xl md:col-span-6 lg:col-span-6">
              <div className="flex flex-col gap-spacing-sm">
                <Typography variant="h4">{title}</Typography>
                <Typography variant="large" className="font-medium text-text-accent-light-dark">
                  {subtitle}
                </Typography>
                <Typography variant="regular" className="mt-spacing-xs">
                  {text}
                </Typography>
                <div
                  className={cn(
                    'justify-left col-span-4 flex w-full flex-wrap gap-spacing-sm pt-spacing-sm md:col-span-6 lg:col-span-6'
                  )}
                >
                  {primary_button?.title ? (
                    <Button asChild className="w-full md:w-auto" variant="primary">
                      <Link href={primary_button!.url} target={primary_button?.target}>
                        <TypographyMask>
                          <Typography variant="regular">{primary_button?.title}</Typography>
                        </TypographyMask>
                      </Link>
                    </Button>
                  ) : null}
                  {secondary_button?.title ? (
                    <Button
                      asChild
                      className="w-full md:w-auto"
                      variant="secondary"
                      endIcon={
                        <SVGIcon
                          className="h-[1.8rem] w-[1.6rem] fill-surface-primary-dark"
                          symbol="arrow-right-2"
                        />
                      }
                    >
                      <Link href={secondary_button!.url} target={secondary_button?.target}>
                        <TypographyMask>
                          <Typography variant="regular">{secondary_button?.title}</Typography>
                        </TypographyMask>
                      </Link>
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="col-span-4 pt-spacing-lg md:col-span-6 lg:col-span-6 lg:pr-spacing-sm">
              <Media {...media} />
            </div>
          </Grid>
        </Container>
      </Section>
    </Motion>
  )
}

export default MediaBanner
